import React from 'react';
import propTypes from 'prop-types';
import { Download } from 'lucide-react';
import { cn } from 'lib/utils';
import { formatMoney } from 'utils/utils';

const TILE_CLASSNAME = 'rounded-xl bg-grey-lightest px-3 py-2';

/**
 * one month of the commissions card; clicking it runs that month's statement
 * @param label - month already formatted for display, e.g. 'August 2026'
 * @param amount - commissions paid that month
 * @param onDownload - runs the month's statement, omit when it can't be run
 * @param disabled - true while a report is already running
 */
export default function CommissionsMonthTile({
  label,
  amount,
  onDownload,
  disabled = false
}) {
  const body = (
    <>
      <p className='m-0 flex min-w-0 items-center gap-1 text-[11px] font-medium text-grey-light'>
        <span className='min-w-0 truncate'>{label}</span>
        {onDownload && <Download className='size-3 shrink-0' aria-hidden />}
      </p>
      <p className='m-0 text-base font-bold tabular-nums text-primary'>
        {formatMoney(Number(amount))}
      </p>
    </>
  );

  if (!onDownload) {
    return <div className={TILE_CLASSNAME}>{body}</div>;
  }

  return (
    <button
      type='button'
      onClick={onDownload}
      disabled={disabled}
      aria-label={`Download the ${label} commission statement`}
      title={
        disabled
          ? 'A report is already running'
          : `Download the ${label} commission statement`
      }
      className={cn(
        TILE_CLASSNAME,
        'cursor-pointer border-0 text-left transition-colors hover:bg-grey-lighter',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        'disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-grey-lightest'
      )}
    >
      {body}
    </button>
  );
}

CommissionsMonthTile.propTypes = {
  label: propTypes.string.isRequired,
  amount: propTypes.oneOfType([propTypes.number, propTypes.string]),
  onDownload: propTypes.func,
  disabled: propTypes.bool
};
